'use client';

import Image from 'next/image';
import React, { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

import { BiCheckCircle, BiDotsHorizontal } from 'react-icons/bi';
import { RiSparkling2Line } from 'react-icons/ri';
import { FiLink } from 'react-icons/fi';
import LouiseButton from '../buttons/LouiseButton';

type Props = { locale: string };

const SectionIntegration = ({ locale }: Props) => {
  const { t } = useTranslation(locale);

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const integrations = [
    {
      name: 'Freshdesk',
      file: '24010911055872_Freshdesk.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911055872_Freshdesk.svg',
      categories: ['support', 'business'],
      description: t('Home/SectionIntegration/freshdesk-desc'),
    },
    {
      name: 'Helpdesk',
      file: '24010911055862_Helpdesk.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911055862_Helpdesk.svg',
      categories: ['support', 'business'],
      description: t('Home/SectionIntegration/helpdesk-desc'),
    },
    {
      name: 'Magento',
      file: '24010911055848_Magento.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911055848_Magento.svg',
      categories: ['website', 'ecommerce', 'business'],
      description: t('Home/SectionIntegration/magento-desc'),
    },
    {
      name: 'Mailchimp',
      file: '24010911055815_Mailchimp.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911055815_Mailchimp.svg',
      categories: ['communication', 'marketing', 'business'],
      description: t('Home/SectionIntegration/mailchimp-desc'),
    },
    {
      name: 'Messenger',
      file: '2401091105581_Messenger.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/2401091105581_Messenger.svg',
      categories: ['communication', 'support'],
      description: t('Home/SectionIntegration/messenger-desc'),
    },
    {
      name: 'Salesforce',
      file: '24010911065113_Salesforce.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911065113_Salesforce.svg',
      categories: ['business', 'marketing', 'support'],
      description: t('Home/SectionIntegration/salesforce-desc'),
    },
    {
      name: 'Shopify',
      file: '24010911065145_Shopify.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911065145_Shopify.svg',
      categories: ['website', 'ecommerce', 'business'],
      description: t('Home/SectionIntegration/shopify-desc'),
    },
    {
      name: 'Slack',
      file: '24010911065123_Slack.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911065123_Slack.svg',
      categories: ['communication', 'business'],
      description: t('Home/SectionIntegration/slack-desc'),
    },
    {
      name: 'Twilio',
      file: '24010911065115_Twilio.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911065115_Twilio.svg',
      categories: ['communication', 'support', 'business'],
      description: t('Home/SectionIntegration/twilio-desc'),
    },
    {
      name: 'WordPress',
      file: '2401091106517_Wordpress.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24042206483316_Wordpress.svg',
      categories: ['website', 'marketing'],
      description: t('Home/SectionIntegration/wordpress-desc'),
    },
    {
      name: 'Zapier',
      file: '24010911065141_Zapier.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911065141_Zapier.svg',
      categories: ['business', 'marketing', 'website'],
      description: t('Home/SectionIntegration/zapier-desc'),
    },
    {
      name: 'Zendesk',
      file: '24010911065194_Zendesk.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24042206483387_Zendesk.svg',
      categories: ['support', 'business'],
      description: t('Home/SectionIntegration/zendesk-desc'),
    },
    {
      name: 'Wix',
      file: '24050512403833_WIX.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24050512403833_WIX.svg',
      categories: ['website', 'ecommerce'],
      description: t('IntegrationsPage/wix/subTitle'),
    },
    {
      name: 'Squarespace',
      file: '24051910363068_squarespace.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24051910363068_squarespace.svg',
      categories: ['website', 'ecommerce'],
      description: t('IntegrationsPage/Squarespace/subTitle'),
    },
    {
      name: 'Reactjs',
      file: '24050512403845_Reacts.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24050512403845_Reacts.svg',
      categories: ['website', 'development'],
      description: t('IntegrationsPage/ReactJS/subTitle'),
    },
    {
      name: 'Angular',
      file: '24050512403852_Angular.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24050512403852_Angular.svg',
      categories: ['website', 'development'],
      description: t('IntegrationsPage/Angular/subTitle'),
    },
    {
      name: 'Html',
      file: '24062311343756_Html_square.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24062311343756_Html_square.svg',
      categories: ['website', 'development'],
      description: t('IntegrationsPage/Html/subTitle'),
    },
    {
      name: 'Bubble',
      file: '24073012280095_Bubble.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24073012280095_Bubble.svg',
      categories: ['website', 'development'],
      description: t('IntegrationsPage/Bubble/subTitle'),
    },
    {
      name: 'Hostinger',
      file: '24113011045130_hostinger_icon.svg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24113011045130_hostinger_icon.svg',
      categories: ['website', 'business'],
      description: t('IntegrationsPage/Hostinger/subTitle'),
    },
    {
      name: 'Brizy',
      file: '24113017071960_brizy_logo.png',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24113017071960_brizy_logo.png',
      categories: ['website', 'development'],
      description: t('IntegrationsPage/Brizy/subTitle'),
    },
    {
      name: 'Youpage',
      file: '24113017354712_youpage_logo.png',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/24113017354712_youpage_logo.png',
      categories: ['website', 'marketing'],
      description: t('IntegrationsPage/YouPage/subTitle'),
    },
    {
      name: 'Webstarts',
      file: '2503311741482_logo_webstart.png',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/2503311741482_logo_webstart.png',
      categories: ['website', 'development'],
      description: t('IntegrationsPage/Webstarts/subTitle'),
    },
    {
      name: 'Durable',
      file: '2504052120327_durable_logo.jpeg',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/2504052120327_durable_logo.jpeg',
      categories: ['website', 'business'],
      description: t('IntegrationsPage/Durable/subTitle'),
    },
    {
      name: 'Google Tag Manager',
      file: '25033119315992_logo_gtm.png',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/25033119315992_logo_gtm.png',
      categories: ['marketing', 'website', 'business'],
      description: t('IntegrationsPage/GoogleTagManager/subTitle'),
    },
    {
      name: 'Prestashop',
      file: '25041321205526_logo_prestashop.png',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/25041321205526_logo_prestashop.png',
      categories: ['website', 'ecommerce', 'business'],
      description: t('Home/SectionIntegration/prestashop-desc'),
    },
    {
      name: 'Make.com',
      file: '25072409575066_logo_makecom.png',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/25072409575066_logo_makecom.png',
      categories: ['business', 'marketing', 'development'],
      description: t('Home/SectionIntegration/makecom-desc'),
    },
    {
      name: 'WhatsApp',
      file: '25072409575045_logo_whatsapp.png',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/25072409575045_logo_whatsapp.png',
      categories: ['communication', 'support', 'marketing', 'business'],
      description: t('Home/SectionIntegration/whatsapp-desc'),
    },
    {
      name: 'Telegram',
      file: '25072409575058_logo_telegram.png',
      cdnPath: 'https://cdn.1cdn.app/application/LOUISEBOT/25072409575058_logo_telegram.png',
      categories: ['communication', 'support'],
      description: t('Home/SectionIntegration/telegram-desc'),
    },
  ];

  const categories = [
    { key: 'all', label: t('Home/SectionIntegration/category-all'), count: integrations.length },
    {
      key: 'communication',
      label: t('Home/SectionIntegration/category-communication'),
      count: integrations.filter((i) => i.categories.includes('communication')).length,
    },
    {
      key: 'website',
      label: t('Home/SectionIntegration/category-website'), // CMS + Développement
      count: integrations.filter((i) => i.categories.includes('website')).length,
    },
    {
      key: 'ecommerce',
      label: t('Home/SectionIntegration/category-ecommerce'),
      count: integrations.filter((i) => i.categories.includes('ecommerce')).length,
    },
    {
      key: 'business',
      label: t('Home/SectionIntegration/category-business'), // CRM + Automation + Support + Outils business
      count: integrations.filter((i) => i.categories.includes('business')).length,
    },
    {
      key: 'marketing',
      label: t('Home/SectionIntegration/category-marketing'),
      count: integrations.filter((i) => i.categories.includes('marketing')).length,
    },
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');
  const filteredIntegrations =
    selectedCategory === 'all'
      ? integrations
      : integrations.filter((item) => item.categories.includes(selectedCategory));

  return (
    <section className="tw-relative tw-pt-16 tw-bg-transparent tw-max-w-7xl">
      <div className="tw-bg-gradient-to-br tw-py-16 lg:tw-py-24 tw-from-gray-900 tw-via-gray-800 tw-to-gray-900 tw-overflow-hidden tw-rounded-3xl">
        {/* Background Effects */}
        <div className="tw-absolute tw-inset-0">
          <div className="tw-absolute tw-bottom-0 tw-right-1/4 tw-w-80 tw-h-80 tw-bg-purple-500/10 tw-rounded-full tw-blur-3xl tw-animate-pulse tw-animation-delay-1000"></div>

          {/* Grid pattern */}
          <div
            className="tw-absolute tw-inset-0 tw-opacity-5"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
              backgroundSize: '50px 50px',
            }}
          ></div>
        </div>

        <div className="tw-relative tw-z-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          {/* Header */}
          <div className="tw-text-center tw-mb-16">
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100/10 tw-backdrop-blur-sm tw-text-blue-400 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6 tw-border tw-border-blue-500/20">
              <FiLink className="tw-w-4 tw-h-4" />
              {t('Home/SectionIntegration/badge')}
            </div>

            <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-white tw-mb-4 tw-font-['Lexend']">
              {t('Home/SectionIntegration/key1')}
            </h2>

            <p className="tw-text-lg tw-text-gray-300 tw-max-w-3xl tw-mx-auto tw-font-['Lexend'] tw-mb-8">
              {t('Home/SectionIntegration/description')}
            </p>

            {/* Stats */}
            <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-8 tw-mb-12">
              <div className="tw-text-center">
                <div className="tw-text-3xl tw-font-bold tw-text-blue-400 tw-font-['Lexend']">
                  {integrations.length}+
                </div>
                <div className="tw-text-gray-400 tw-text-sm tw-font-['Lexend']">
                  {t('Home/SectionIntegration/stat-integrations')}
                </div>
              </div>
              <div className="tw-text-center">
                <div className="tw-text-3xl tw-font-bold tw-text-green-400 tw-font-['Lexend']">{'< 5'}</div>
                <div className="tw-text-gray-400 tw-text-sm tw-font-['Lexend']">
                  {t('Home/SectionIntegration/stat-setup-time')}
                </div>
              </div>
              <div className="tw-text-center">
                <div className="tw-text-3xl tw-font-bold tw-text-purple-400 tw-font-['Lexend']">24/7</div>
                <div className="tw-text-gray-400 tw-text-sm tw-font-['Lexend']">
                  {t('Home/SectionIntegration/availability')}
                </div>
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2 tw-mb-12">
            {categories.map((category) => (
              <button
                key={category.key}
                onClick={() => setSelectedCategory(category.key)}
                className={`tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-font-['Lexend'] tw-transition-all tw-duration-300 ${
                  selectedCategory === category.key
                    ? 'tw-bg-blue-600 tw-text-white tw-shadow-lg'
                    : 'tw-bg-white/10 tw-text-gray-300 hover:tw-bg-white/20 tw-backdrop-blur-sm'
                }`}
              >
                {category.label} ({category.count})
              </button>
            ))}
          </div>

          {/* Integrations Grid */}
          <div className="tw-grid grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-5 xl:tw-grid-cols-7 tw-gap-6 tw-mb-16">
            {filteredIntegrations.map((integration, index) => (
              <div
                key={integration.name}
                className="tw-group tw-relative tw-bg-white/5 tw-backdrop-blur-sm tw-rounded-2xl tw-p-6 tw-border tw-border-white/10 tw-transition-all tw-duration-300 hover:tw-bg-white/10 hover:tw-scale-105 hover:tw-shadow-2xl tw-cursor-pointer"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Integration Logo */}
                <div className="tw-flex tw-items-center tw-justify-center tw-h-16 tw-mb-4">
                  <Image
                    priority={index < 8}
                    quality={100}
                    alt={`${integration.name} integration`}
                    src={integration.cdnPath}
                    width={60}
                    height={40}
                    className="tw-max-w-full tw-max-h-full tw-object-contain tw-transition-all tw-duration-300 group-hover:tw-scale-110"
                  />
                </div>

                {/* Integration Name */}
                <div className="tw-text-center">
                  <div className="tw-text-white tw-font-semibold tw-font-['Lexend'] tw-text-sm tw-mb-1">
                    {integration.name}
                  </div>
                  <div className="tw-text-gray-400 tw-text-xs tw-font-['Lexend'] tw-capitalize">
                    {integration.categories[0]}
                  </div>
                </div>

                {/* Hover Overlay */}
                <div
                  className={`tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-blue-600/90 tw-to-purple-600/90 tw-rounded-2xl tw-p-4 tw-flex tw-flex-col tw-justify-center tw-items-center tw-text-center tw-transition-all tw-duration-300 ${
                    hoveredIndex === index ? 'tw-opacity-100' : 'tw-opacity-0'
                  }`}
                >
                  <div className="tw-text-white tw-font-bold tw-font-['Lexend'] tw-text-sm tw-mb-2">
                    {integration.name}
                  </div>
                  <div className="tw-text-blue-100 tw-text-xs tw-font-['Lexend'] tw-leading-relaxed tw-mb-3">
                    {integration.description}
                  </div>
                </div>

                {/* Status indicator */}
                <div className="tw-absolute tw-top-2 tw-right-2">
                  <BiCheckCircle className="tw-w-4 tw-h-4 tw-text-green-400" />
                </div>
              </div>
            ))}

            {/* Add More Card */}
            <div className="tw-group tw-bg-white/5 tw-backdrop-blur-sm tw-rounded-2xl tw-p-6 tw-border tw-border-dashed tw-border-white/20 tw-transition-all tw-duration-300 hover:tw-bg-white/10 tw-cursor-pointer tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
              <BiDotsHorizontal className="tw-w-8 tw-h-8 tw-text-gray-400 group-hover:tw-text-white tw-transition-colors tw-duration-300 tw-mb-2" />
              <div className="tw-text-gray-400 group-hover:tw-text-white tw-font-semibold tw-font-['Lexend'] tw-text-sm tw-transition-colors tw-duration-300">
                {t('Home/SectionIntegration/integration-more')}
              </div>
            </div>
          </div>
          {/* Bottom Section */}
          <div className="tw-text-center tw-space-y-8">
            <div className="tw-bg-white/5 tw-backdrop-blur-sm tw-rounded-2xl tw-p-8 tw-border tw-border-white/10">
              <h3 className="tw-text-xl tw-font-bold tw-text-white tw-mb-4 tw-font-['Lexend']">
                {t('Home/SectionIntegration/key2')}
              </h3>
              <p className="tw-text-gray-300 tw-font-['Lexend'] tw-mb-6">
                {t('Home/SectionIntegration/custom-integration-desc')}
              </p>
              <LouiseButton
                color="blue"
                cta={t('Home/SectionIntegration/contact-us')}
                ctaTarget="contact"
                locale={locale}
                gradiant={true}
              />
            </div>

            {/* Final CTA */}
            <div>
              <h4 className="tw-text-2xl tw-font-bold tw-text-white tw-mb-4 tw-font-['Lexend']">
                {t('Home/SectionIntegration/cta-title')}
              </h4>
              <p className="tw-text-gray-300 tw-font-['Lexend'] tw-mb-8 tw-max-w-2xl tw-mx-auto">
                {t('Home/SectionIntegration/cta-description')}
              </p>
              <LouiseButton
                color="gray-light"
                icon={<RiSparkling2Line className="tw-w-5 tw-h-5" />}
                cta={t('Home/SectionIntegration/cta-button')}
                ctaTarget="register"
                locale={locale}
              />
            </div>
          </div>
        </div>

        <style jsx>{`
          .tw-animation-delay-1000 {
            animation-delay: 1000ms;
          }
        `}</style>
      </div>
    </section>
  );
};

export default SectionIntegration;
